import { useEffect, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import CancelModalLS from './CancelModalLS/CancelModalLS';
import DelayedModalLS from './DelayedModalLS/DelayedModalLS';
import {
  DTOupdateLogisticServiceStatus,
  IlogisticServiceTrelloCardData,
  LOGISTIC_DROPPABLE_ID
} from '../../../../types/services';
import DeliveredLogisticsModal from './DeliveredLogisticsModal/DeliveredLogisticsModal';
import { updateStatusService } from '../../../../services/services';
import ConfirmedToActiveModalLS from './ConfirmedToActiveModalLS/ConfirmedToActiveModalLS';
import { getLogisticServiceDetails } from '../../../LogisticServices/utils/logisticServices.utils';
import { NudosGenericModal } from '../../../../components/NudosComponents';
import PendingToConfirmedModalLS from './PendingToConfirmedModalLS/PendingToConfirmedModalLS';
import ConfirmedToProcess from './ConfirmedToProcess/ConfirmedToProcess';
import useLsTrelloState from '../../../../state/useLsTrelloState';
import useOnlyDevelopers from '../../../../state/setOnlyDevelopers';
import { OffboardingToWarehouseModal } from './OffboardingToWarehouseModal/OffboardingToWarehouseModal';
import { checkDestinationIsWarehouse } from '../../../../utils/logisticServiceOriginDestination.utils';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import {
  handleConfigurationTransitionBlockedApiError,
  GO_TO_CONFIGURATIONS_BUTTON_TEXT,
  LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE
} from 'utils/productConfigurationSummary.utils';

const LogisticsServicesTrelloModals = ({
  selectedLogisticServiceDataForModal,
  setSelectedLogisticServiceDataForModal,
  cancelModalData,
  delayedModalData,
  activeModalData,
  deliveredModalData,
  confirmModalData,
  processModalData,
  closeAndRestoreList
}: {
  selectedLogisticServiceDataForModal?: IlogisticServiceTrelloCardData;
  setSelectedLogisticServiceDataForModal: React.Dispatch<
    React.SetStateAction<IlogisticServiceTrelloCardData | undefined>
  >;
  cancelModalData: IlogisticServicesTrelloModalData;
  confirmModalData: IlogisticServicesTrelloModalData;
  processModalData: IlogisticServicesTrelloModalData;
  delayedModalData: IlogisticServicesTrelloModalData;
  activeModalData: IlogisticServicesTrelloModalData;
  deliveredModalData: IlogisticServicesTrelloModalData;
  closeAndRestoreList: () => void;
}) => {
  const { setUpdatedCounter, updatedCounter, dragEndData } = useLsTrelloState();
  const { isOnlyDevsLogistics } = useOnlyDevelopers();
  const [logisticServiceDetails, setLogisticServiceDetails] = useState<{ [key: string]: any }>();
  const [loadingLogisticServiceDetails, setLoadingLogisticServiceDetails] = useState(true);
  const [loadingChangeLSStatus, setLoadingChangeLSStatus] = useState<boolean>(false);
  const { CANCELLED_ID, CONFIRMED_ID, ACTIVE_ID, DELIVERED_ID, INPROCESS_ID, DELAYED_ID, TOCONFIRMED_ID } =
    LOGISTIC_DROPPABLE_ID;

  const isDestinationWarehouse = checkDestinationIsWarehouse(selectedLogisticServiceDataForModal);

  const handleSuccessInUpdatingStatus = async (closeModalCallback?: () => void) => {
    setSelectedLogisticServiceDataForModal(undefined);
    closeModalCallback && closeModalCallback();
    setUpdatedCounter({
      ...updatedCounter,
      atrasado: updatedCounter?.atrasado + 1
    });
  };

  const updateDataColumnByState = () => {
    const originState = dragEndData?.source?.droppableId || '';
    const destinationState = dragEndData?.destination?.droppableId || '';
    const getPropertyName = (originDestination: string) => {
      switch (originDestination) {
        case INPROCESS_ID:
          return 'en_proceso';
        case TOCONFIRMED_ID:
          return 'por_confirmar';
        case DELIVERED_ID:
          return 'entregado';
        case DELAYED_ID:
          return 'atrasado';
        case ACTIVE_ID:
          return 'activo';
        case CONFIRMED_ID:
          return 'confirmado';
        case CANCELLED_ID:
          return 'cancelado';
        default:
          return '';
      }
    };
    const originProperty = getPropertyName(originState);
    const destinationProperty = getPropertyName(destinationState);
    setUpdatedCounter({
      ...updatedCounter,
      [destinationProperty]: updatedCounter?.[destinationProperty] + 1,
      [originProperty]: updatedCounter?.[originProperty] + 1
    });
  };

  const handleSuccessAfterStatusUpdate = async (closeModalCallback?: () => void) => {
    setLoadingChangeLSStatus(true);
    try {
      await handleSuccessInUpdatingStatus(closeModalCallback);
      updateDataColumnByState();
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar el estado del servicio logístico</>
      });
    } finally {
      setLogisticServiceDetails(undefined);
      setLoadingChangeLSStatus(false);
    }
  };

  const changeLogisticServiceStatus = async (
    body?: DTOupdateLogisticServiceStatus,
    closeModalCallback?: () => void,
    paymentFile?: File,
    invoiceFile?: File
  ) => {
    if (!selectedLogisticServiceDataForModal?.id) return;
    setLoadingChangeLSStatus(true);
    try {
      await updateStatusService(selectedLogisticServiceDataForModal.id, body, paymentFile, invoiceFile);
      await handleSuccessInUpdatingStatus(closeModalCallback);
      updateDataColumnByState();
    } catch (error) {
      const handledConfigurationBlock =
        isOnlyDevsLogistics &&
        handleConfigurationTransitionBlockedApiError(
          error,
          LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE,
          displayErrorNotification,
          GO_TO_CONFIGURATIONS_BUTTON_TEXT,
          () => {
            if (selectedLogisticServiceDataForModal?.id) {
              window.location.href = `/logistic-service/details/${selectedLogisticServiceDataForModal.id}?initialTab=2`;
            }
          }
        );

      if (!handledConfigurationBlock) {
        displayErrorNotification();
      }
    } finally {
      setLogisticServiceDetails(undefined);
      setLoadingChangeLSStatus(false);
    }
  };

  const handleErrorGettingLogisticServiceDetails = () => {
    displayErrorNotification();
    setSelectedLogisticServiceDataForModal(undefined);
    setLogisticServiceDetails(undefined);
    closeAndRestoreList();
  };

  const logisticServiceDetailsLoader = (
    <NudosGenericModal
      modalContent={
        <div className="w-screen h-screen flex items-center content-center justify-center">
          <LoadingOutlined style={{ color: '#070F21', fontSize: '50px' }} />
        </div>
      }
    />
  );

  const extendedCloseAndRestoreListCallback = () => {
    closeAndRestoreList();
    setLogisticServiceDetails(undefined);
  };

  const refreshLogisticServiceDetails = () => {
    if (!selectedLogisticServiceDataForModal?.id) return;

    getLogisticServiceDetails(
      selectedLogisticServiceDataForModal.id,
      setLogisticServiceDetails,
      setLoadingLogisticServiceDetails,
      handleErrorGettingLogisticServiceDetails
    );
  };

  useEffect(() => {
    if (!selectedLogisticServiceDataForModal || !selectedLogisticServiceDataForModal?.id) return;
    getLogisticServiceDetails(
      selectedLogisticServiceDataForModal?.id,
      setLogisticServiceDetails,
      setLoadingLogisticServiceDetails,
      handleErrorGettingLogisticServiceDetails
    );
  }, [selectedLogisticServiceDataForModal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedLogisticServiceDataForModal) return <></>;
  if (loadingLogisticServiceDetails || !logisticServiceDetails) return logisticServiceDetailsLoader;

  return (
    <div className="LogisticsServicesTrelloModals">
      {cancelModalData.showModal && (
        <CancelModalLS
          closeModal={cancelModalData?.closeModal}
          updateDataCallBack={changeLogisticServiceStatus}
          onlyItemCard={selectedLogisticServiceDataForModal}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          loading={loadingChangeLSStatus}
        />
      )}
      {confirmModalData?.showModal && (
        <PendingToConfirmedModalLS
          onlyItemCard={selectedLogisticServiceDataForModal}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          logisticServiceDetails={logisticServiceDetails}
          changeLogisticServiceStatusCallback={changeLogisticServiceStatus}
          loadingChangeLSStatus={loadingChangeLSStatus}
          closeModal={confirmModalData?.closeModal}
        />
      )}
      {processModalData.showModal && (
        <ConfirmedToProcess
          closeModal={processModalData?.closeModal}
          onlyItemCard={selectedLogisticServiceDataForModal}
          logisticServiceDetails={logisticServiceDetails}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          changeLogisticServiceStatus={changeLogisticServiceStatus}
          modalButtonIsLoading={loadingChangeLSStatus}
        />
      )}
      {delayedModalData.showModal && (
        <DelayedModalLS
          handleSuccessInUpdatingStatus={handleSuccessInUpdatingStatus}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          logisticServiceDetails={logisticServiceDetails}
          closeModalAfterSubmitCallback={delayedModalData?.closeModal}
          selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
          refreshLogisticServiceDetails={refreshLogisticServiceDetails}
        />
      )}
      {deliveredModalData.showModal && !isDestinationWarehouse && (
        <DeliveredLogisticsModal
          selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
          logisticServiceDetails={logisticServiceDetails}
          closeModalAfterSubmitCallback={deliveredModalData?.closeModal}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          changeLogisticServiceStatusCallback={changeLogisticServiceStatus}
          modalButtonIsLoading={loadingChangeLSStatus}
        />
      )}
      {deliveredModalData.showModal && isDestinationWarehouse && (
        <OffboardingToWarehouseModal
          cardData={selectedLogisticServiceDataForModal}
          serviceDetails={logisticServiceDetails}
          closeModalCallback={extendedCloseAndRestoreListCallback}
          closeModalAfterSubmitCallback={() => handleSuccessAfterStatusUpdate(deliveredModalData?.closeModal)}
        />
      )}
      {activeModalData.showModal && (
        <ConfirmedToActiveModalLS
          updateDataCallback={changeLogisticServiceStatus}
          closeAndRestoreList={extendedCloseAndRestoreListCallback}
          logisticServiceDetails={logisticServiceDetails}
          closeModalAfterSubmitCallback={activeModalData?.closeModal}
          selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
          modalButtonIsLoading={loadingChangeLSStatus}
        />
      )}
    </div>
  );
};
export default LogisticsServicesTrelloModals;
export interface IlogisticServicesTrelloModalData {
  showModal: boolean;
  closeModal: () => void;
}
export type TchangeLogisticServiceStatusCallback = (
  body?: DTOupdateLogisticServiceStatus,
  closeModalCallback?: () => void,
  paymentFile?: File,
  invoiceFile?: File
) => Promise<void>;
